import { useMemo } from "react";

import { keyBy } from "lodash-es";

import { useNavigableWorkspaces } from "src/api/queries";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { OrganizationUserWorkspaceRole } from "src/store/api/types";
import { CellRendererProps } from "src/userManagement/types";

const WORKSPACE_ROLES: WorkspaceSimpleRoleType[] = Object.values(
  WorkspaceSimpleRoleType,
);

export const WORKSPACE_ROLES_NAMES: Record<WorkspaceSimpleRoleType, string> = {
  ADMIN: "Admin",
  EDITOR: "Editor",
  VIEWER: "Viewer",
  REVIEWER: "Reviewer",
};

const splitNames = (names: string[]) => {
  const [first, rest] = [names.slice(0, 2), names.slice(2)];

  return {
    all: names.join(", "),
    first: first.join(", "),
    rest: rest.join(", "),
    more: rest.length > 0 ? ` +${rest.length} more` : "",
  };
};

export const WorkspaceRolesRenderer = ({
  getValue,
  row,
}: CellRendererProps<OrganizationUserWorkspaceRole[]>) => {
  const workspaces = useNavigableWorkspaces();

  const userWorkspaceRoles = getValue();
  const workspacesById: Record<string, WorkspaceDataplane> = useMemo(
    () => (workspaces.data ? keyBy(workspaces.data, "id") : {}),
    [workspaces],
  );

  if (row.original.status === "deactivated") {
    return (
      <span className="block py-2 pl-2 text-left font-inter-normal-12px font-medium text-gray-800">
        No Access
      </span>
    );
  }

  const userWorkspacesByRole = (userWorkspaceRoles || []).reduce(
    (acc, { role, workspace_id }) => ({
      ...acc,
      [role]: [...(acc[role] || []), workspace_id],
    }),
    {} as Record<WorkspaceSimpleRoleType, string[]>,
  );

  return (
    <div className="flex items-center gap-1.5 pl-2 text-left">
      {WORKSPACE_ROLES.map((role) => {
        const workspaceIds = userWorkspacesByRole[role];

        if (workspaceIds) {
          const workspaceNames: string[] = workspaceIds
            .map((wsId) => workspacesById[wsId]?.name ?? "")
            .filter((name) => Boolean(name));

          if (workspaceNames.length > 0) {
            const names = splitNames(workspaceNames);
            return (
              <Tooltip
                key={role}
                delay={200}
                disabled={!names.rest}
                placement="top"
                title={names.rest}
                asChild
              >
                <div>
                  <Pill
                    fontType="text"
                    dataLoc={`workspaces-role-${role}-user-${row.original.username}`}
                    size="sm"
                    variant="gray"
                  >
                    <Pill.Text>
                      <span className="text-2xs text-gray-500">
                        {WORKSPACE_ROLES_NAMES[role]} at
                      </span>
                      {` ${names.first + names.more}`}
                    </Pill.Text>
                  </Pill>
                </div>
              </Tooltip>
            );
          }
        }

        return null;
      })}
    </div>
  );
};
